import ReactHtmlParser from 'react-html-parser';

import { useIntl } from 'react-intl';
import React from 'react';
import { Phone } from '../../../../types';
import {
  Specs,
  MainTitle,
  SpecSectionTitle,
  SpecSection,
  SpecSectionLabel,
  SpecSectionDetail,
  SpecRow,
  SpecRoundEdgeBox,
  SpecBottomLine,
} from './styles';

interface Props {
  phone: Phone;
  specLabels: Record<string, string>;
}

const SpecsChart = ({ phone, specLabels }: Props) => {
  const { locale } = useIntl();
  const phoneDimensions = `${phone.phoneLength}in(H) x ${phone.phoneWidth}in(W) x ${phone.phoneThickness}in(D)`;
  const yesText = locale === 'en' ? 'Yes' : 'Oui';
  const noText = locale === 'en' ? 'No' : 'Non';

  return (
    <Specs>
      {/* DETAILS AND SPECS */}
      <MainTitle>
        {ReactHtmlParser(specLabels['44dndBdF7r1y04NRIABANj'])}
      </MainTitle>

      {/* GENERAL SPECS */}
      <SpecSectionTitle>
        {ReactHtmlParser(specLabels['4uaM7suEV9Vh5tZX4MnCbo'])}
      </SpecSectionTitle>
      <SpecRoundEdgeBox>
        <SpecSection>
          {/* DIMENSIONS */}
          <SpecRow>
            <SpecSectionLabel>
              {ReactHtmlParser(specLabels['7l2rhJkWTdMH9l1OSxOVi5'])}
            </SpecSectionLabel>
            <SpecSectionDetail>{phoneDimensions}</SpecSectionDetail>
          </SpecRow>
          <SpecBottomLine />

          {/* WEIGHT */}
          <SpecRow>
            <SpecSectionLabel>
              {ReactHtmlParser(specLabels['7bAiZ59KO1ItHRWoa3CyD2'])}
            </SpecSectionLabel>
            <SpecSectionDetail>
              {`${ReactHtmlParser(phone.phoneWeight)} ounces`}
            </SpecSectionDetail>
          </SpecRow>
          <SpecBottomLine />

          {/* OPERATING SYSTEM */}
          <SpecRow>
            <SpecSectionLabel>
              {ReactHtmlParser(specLabels['6YOz3OY8XrTs90mgEPvXyc'])}
            </SpecSectionLabel>
            <SpecSectionDetail>
              {ReactHtmlParser(phone.osType)}
            </SpecSectionDetail>
          </SpecRow>
        </SpecSection>
      </SpecRoundEdgeBox>

      {/* DISPLAY AND INTERFACE */}
      <SpecSectionTitle>
        {ReactHtmlParser(specLabels['3PnWieTFUmLGJD9IIm6zei'])}
      </SpecSectionTitle>

      <SpecRoundEdgeBox>
        <SpecSection>
          {/*  DISPLAY SIZE */}
          <SpecRow>
            <SpecSectionLabel>
              {ReactHtmlParser(specLabels['2ACJSBZcWy8PAmCRShj0Ub'])}
            </SpecSectionLabel>
            <SpecSectionDetail>
              {`${ReactHtmlParser(phone.displaySize)}in`}
            </SpecSectionDetail>
          </SpecRow>
          <SpecBottomLine />

          {/* DISPLAY TECH */}
          <SpecRow>
            <SpecSectionLabel>
              {ReactHtmlParser(specLabels['1d9wdQQqlP3YOu65X4bqkr'])}
            </SpecSectionLabel>
            <SpecSectionDetail>
              {ReactHtmlParser(phone.displayTechnology)}
            </SpecSectionDetail>
          </SpecRow>
          <SpecBottomLine />

          {/* PIXEL DENSITY */}
          <SpecRow>
            <SpecSectionLabel>
              {ReactHtmlParser(specLabels['6XniqftH47Re4WZSi4DSjJ'])}
            </SpecSectionLabel>
            <SpecSectionDetail>
              {`${ReactHtmlParser(phone.displayPixelDensity)} PPI`}
            </SpecSectionDetail>
          </SpecRow>
        </SpecSection>
      </SpecRoundEdgeBox>

      {/* BATTERY */}
      <SpecSectionTitle>
        {ReactHtmlParser(specLabels['4sRoPtv6LfXRAcvxEtEd72'])}
      </SpecSectionTitle>

      <SpecRoundEdgeBox>
        <SpecSection>
          {/*  CAPACITY */}
          <SpecRow>
            <SpecSectionLabel>
              {ReactHtmlParser(specLabels['3U9YzG6khzzSICsyqyf3xt'])}
            </SpecSectionLabel>
            <SpecSectionDetail>
              {`${ReactHtmlParser(phone.batteryCapacity)} mAh`}
            </SpecSectionDetail>
          </SpecRow>
          <SpecBottomLine />

          {/* WIRELESS CHARGING */}
          <SpecRow>
            <SpecSectionLabel>
              {ReactHtmlParser(specLabels['3pn3F8qE1X7KOaZojjabp7'])}
            </SpecSectionLabel>
            <SpecSectionDetail>
              {phone.batteryWirelessCharging === true ? yesText : noText}
            </SpecSectionDetail>
          </SpecRow>
        </SpecSection>
      </SpecRoundEdgeBox>

      {/* CAMERA */}
      <SpecSectionTitle>
        {ReactHtmlParser(specLabels['3AibzFaNwxCuF1j3dWvGrn'])}
      </SpecSectionTitle>

      <SpecRoundEdgeBox>
        <SpecSection>
          {/*  REAR CAMERA */}
          <SpecRow>
            <SpecSectionLabel>
              {ReactHtmlParser(specLabels.zKqS3wYHOBg4Wozm74FNN)}
            </SpecSectionLabel>
            <SpecSectionDetail>
              {`${ReactHtmlParser(phone.rearCameraResolution)} MP`}
            </SpecSectionDetail>
          </SpecRow>
          <SpecBottomLine />

          {/* REAR FETAURES */}
          <SpecRow>
            <SpecSectionLabel>
              {ReactHtmlParser(specLabels['6cITYZUxfadgSSGwyDq8KI'])}
            </SpecSectionLabel>
            <SpecSectionDetail>
              {phone.rearCameraFeatures
                ? ReactHtmlParser(phone.rearCameraFeatures.rearCameraFeatures)
                : 'N/A'}
            </SpecSectionDetail>
          </SpecRow>
          <SpecBottomLine />

          {/*  FRONT CAMERA */}
          <SpecRow>
            <SpecSectionLabel>
              {ReactHtmlParser(specLabels['4fy3cIaDvBsJOMgIOIjHXi'])}
            </SpecSectionLabel>
            <SpecSectionDetail>
              {`${ReactHtmlParser(phone.frontCameraResolution)} MP`}
            </SpecSectionDetail>
          </SpecRow>
          <SpecBottomLine />

          {/* FRONT FETAURES */}
          <SpecRow>
            <SpecSectionLabel>
              {ReactHtmlParser(specLabels['1gU6ECOqdsiFDGNUOLk2OT'])}
            </SpecSectionLabel>
            <SpecSectionDetail>
              {phone.frontCameraFeatures
                ? ReactHtmlParser(phone.frontCameraFeatures.frontCameraFeatures)
                : 'N/A'}
            </SpecSectionDetail>
          </SpecRow>
        </SpecSection>
      </SpecRoundEdgeBox>
    </Specs>
  );
};

export default SpecsChart;
